// frontend/src/context/CartContext.jsx - Updated with retry logic
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getCart,
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart,
} from "../api";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

const INITIAL_CART_STATE = {
  items: [],
  totalAmount: { usd: 0, sll: 0 },
  summary: {
    subtotal: { usd: 0, sll: 0 },
    discount: { usd: 0, sll: 0 },
    shipping: { usd: 0, sll: 0 },
    total: { usd: 0, sll: 0 },
  },
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(INITIAL_CART_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart(INITIAL_CART_STATE);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await getCart();
      setCart(data || INITIAL_CART_STATE);
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching cart:", err);

      // If we get a 400 or 404, retry up to 2 times
      if (
        (err.response?.status === 400 || err.response?.status === 404) &&
        retryCount < 2
      ) {
        setRetryCount((prev) => prev + 1);
        // Wait a bit before retrying
        setTimeout(() => {
          fetchCart();
        }, 500);
        return;
      }

      setError(err.response?.data?.message || "Failed to load cart");
      setCart(INITIAL_CART_STATE);
    } finally {
      setLoading(false);
    }
  }, [user, retryCount]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addItem = useCallback(
    async (productId, quantity) => {
      if (!user) {
        console.warn("Cannot add to cart: User not logged in");
        return false;
      }
      setError(null);
      try {
        const { data } = await addToCart(productId, quantity);
        setCart(data);
        return true;
      } catch (err) {
        console.error("Error adding to cart:", err);

        // If we get a 400, retry once
        if (err.response?.status === 400) {
          try {
            // Try to get cart again first
            const { data: cartData } = await getCart();
            if (cartData) {
              setCart(cartData);
              // Retry adding
              const { data } = await addToCart(productId, quantity);
              setCart(data);
              return true;
            }
          } catch (retryErr) {
            console.error("Retry failed:", retryErr);
          }
        }

        const message =
          err.response?.data?.message || "Failed to add item to cart";
        setError(message);
        throw new Error(message);
      }
    },
    [user],
  );

  const updateItem = useCallback(
    async (itemId, quantity) => {
      if (!user) return;
      setError(null);
      try {
        const { data } = await updateCartItem(itemId, quantity);
        setCart(data);
      } catch (err) {
        console.error("Error updating cart:", err);
        const message = err.response?.data?.message || "Failed to update cart";
        setError(message);
        throw new Error(message);
      }
    },
    [user],
  );

  const removeItem = useCallback(
    async (itemId) => {
      if (!user) return;
      setError(null);
      try {
        const { data } = await removeFromCart(itemId);
        setCart(data);
      } catch (err) {
        console.error("Error removing from cart:", err);
        const message =
          err.response?.data?.message || "Failed to remove item from cart";
        setError(message);
        throw new Error(message);
      }
    },
    [user],
  );

  const emptyCart = useCallback(async () => {
    if (!user) return;
    setError(null);
    try {
      await clearCart();
      setCart(INITIAL_CART_STATE);
    } catch (err) {
      console.error("Error clearing cart:", err);
      const message = err.response?.data?.message || "Failed to clear cart";
      setError(message);
    }
  }, [user]);

  const getCartCount = useCallback(() => {
    return (
      cart.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
    );
  }, [cart.items]);

  const getCartTotal = useCallback(() => {
    return cart.summary?.total?.usd || cart.totalAmount?.usd || 0;
  }, [cart.summary, cart.totalAmount]);

  const getItemCount = useCallback(
    (productId) => {
      if (!cart.items) return 0;
      const item = cart.items.find(
        (i) =>
          i.product?._id === productId ||
          i.productId === productId ||
          i.product === productId,
      );
      return item?.quantity || 0;
    },
    [cart.items],
  );

  const getCartSubtotal = useCallback(() => {
    if (!cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      const price =
        item.finalPrice?.usd ||
        item.price?.usd ||
        item.product?.basePrice?.usd ||
        0;
      return sum + price * (item.quantity || 1);
    }, 0);
  }, [cart.items]);

  const getCartItems = useCallback(() => {
    return cart.items || [];
  }, [cart.items]);

  const isItemPreOrder = useCallback(
    (productId) => {
      if (!cart.items) return false;
      const item = cart.items.find(
        (i) =>
          i.product?._id === productId ||
          i.productId === productId ||
          i.product === productId,
      );
      return item?.isPreOrder || false;
    },
    [cart.items],
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        addItem,
        updateItem,
        removeItem,
        emptyCart,
        fetchCart,
        getCartCount,
        getCartTotal,
        getItemCount,
        getCartSubtotal,
        getCartItems,
        isItemPreOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
